/**
 * «Анализ» (`/analysis`, `/analysis/<имя>`): разовые разборы с цифрами отдельными страницами —
 * в чат уходит ссылка, а не простыня. Текст — markdown из `docs/analysis/` (`GET /api/analysis`).
 * Приватный разбор открывается только со своим `?k=` и в списке не показывается
 * (`docs/specs/2026-10-10-analysis-private-design.md`).
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router";

import { Prose } from "@/components/Prose";
import { apiFetch } from "@/lib/api";

type Item = { slug: string; date: string; title: string };
type Page = Item & { markdown: string; private: boolean };

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
/** «2026-10-01» → «01.10.2026». */
const day = (iso: string) => iso.split("-").reverse().join(".");

function List() {
  const q = useQuery({ queryKey: ["analysis"], queryFn: () => apiFetch<Item[]>("/api/analysis") });
  if (q.isPending) return <p className="text-fg-muted">Загрузка…</p>;
  if (q.isError) return <p className="text-err">Не удалось загрузить список</p>;
  if (!q.data.length) return <p className="text-fg-muted">Разборов пока нет</p>;
  return (
    <div className="flex max-w-[860px] flex-col gap-2">
      {q.data.map((a) => (
        <Link key={a.slug} to={`/analysis/${a.slug}`} className={`${card} flex items-baseline gap-3 px-4 py-3 hover:border-fg-faint`}>
          <span className="shrink-0 text-[12px] tabular-nums text-fg-muted">{day(a.date)}</span>
          <span className="font-medium">{a.title}</span>
        </Link>
      ))}
    </div>
  );
}

function Article({ slug }: { slug: string }) {
  const [params] = useSearchParams();
  // Хэш приватного разбора: без него ручка отвечает «нет такого разбора».
  const k = params.get("k") ?? "";
  const q = useQuery({
    queryKey: ["analysis", slug, k],
    queryFn: () => apiFetch<Page>(`/api/analysis/${slug}${k ? `?k=${encodeURIComponent(k)}` : ""}`),
  });
  if (q.isPending) return <p className="text-fg-muted">Загрузка…</p>;
  if (q.isError) return <p className="text-err">Такого разбора нет</p>;
  return (
    <article className="max-w-[920px] text-[14px] leading-[1.6]">
      <div className="flex flex-wrap items-baseline gap-3">
        <Link to="/analysis" className="text-[12px] text-fg-muted hover:text-fg">
          ← Все разборы · {day(q.data.date)}
        </Link>
        {q.data.private && (
          <span
            className="rounded-[var(--radius-ui)] border border-border px-1.5 py-0.5 text-[11.5px] text-fg-muted"
            title="Разбора нет в списке «Анализа»: он открывается только по этой ссылке с хэшем"
            data-annot="метка приватного разбора"
          >
            приватная ссылка — в списке разборов нет
          </span>
        )}
      </div>
      <Prose>{q.data.markdown}</Prose>
    </article>
  );
}

export function AnalysisPage() {
  const { slug } = useParams();
  // Каркас кабинета не листается сам: прокрутку держит `<main>` каждой страницы.
  return (
    <main className="min-h-0 flex-1 overflow-y-auto p-6">
      {slug ? <Article slug={slug} /> : <List />}
    </main>
  );
}
